import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from '@nykaa/intl-shared';
import { useDispatch, useSelector } from 'react-redux';

import { LOG_IN } from 'constants/strings';
import { useMenuItems } from 'hooks/header';
import { ACCOUNT, LOGIN_CLICK_EVENT, userSessionTypes } from 'shared/constants';
import autoSignIn from 'shared/store/smartLock/autoSignin';
import { showSignUp } from 'shared/store/app/actions';
import { redirectAuthPage, handleVerloopChat } from 'shared/helpers/auth';
import Item from './Item';
import { Region } from 'types';
import { checkIsIndianUser } from 'helpers/utils/user';
import Toast, { VARIANT } from '@nykaa/ui-components/Toast';
import { setLoginLocation } from 'store/auth/helper';
import { LOGIN_LOCATION } from 'constants/auth';
import { pushEvent } from 'shared/helpers/utils/dataLayer';
import { State } from 'store/types';
import {
  authPageInteractionLocation,
  getPageType,
} from 'components/Header/helper';
import {
  fetchRequestId,
  fetchTrueCallerStatus,
} from 'shared/store/app/actions';
import { getDeviceType } from 'components/Analytics/Initialize/helpers';
import { fetchTrueCallerScript } from 'shared/helpers/utils/autoFill';
import { IOS } from 'shared/constants/devices';
import { useTruecallerStatus } from 'shared/hooks/auth';

const LoginLink = styled.a`
  height: 44px;
  line-height: 44px;
`;

const Button = styled.button`
  background: transparent;
  border: none;
  border-radius: none;
  width: 100%;
  text-align: left;
`;

interface GuestUserProps {
  setShowZendeskChat: React.Dispatch<React.SetStateAction<boolean>>;
  isSmartLockActive: boolean;
  listItemClass: string;
  toggleMenu: () => void;
  isAutoSignUp: boolean;
  showChatNow: boolean;
  closeMenu: () => void;
  region: Region;
}

function GuestUser({
  listItemClass,
  toggleMenu,
  isSmartLockActive,
  isAutoSignUp,
  showChatNow,
  closeMenu,
  region,
  setShowZendeskChat,
}: GuestUserProps) {
  const { pathname } = useLocation();
  const menuItemsMap = useMenuItems();
  const menuItems = menuItemsMap[userSessionTypes.GUEST];
  const isIndianUser = checkIsIndianUser(region.regionHeaderCountry);
  const [toastMsg, setToastMsg] = useState('');
  const isZendeskEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.zendeskChatEnabled || false
  );
  const [tcInitiated, setTcInitiated] = useState(false);
  const dispatch = useDispatch();
  const { device, trueCallerConfig, isStatusFetching, isTrueCallerEnabled } =
    useTruecallerStatus();

  const loadTruecallerScript = () => {
    const isDeviceIos = getDeviceType() === IOS;
    if (!isDeviceIos) {
      fetchTrueCallerScript({
        fetchRequestId,
        setGoogleOneTap: () => {},
        fetchTrueCallerStatus,
        trueCallerConfig,
        isStatusFetching,
        isTrueCallerEnabled,
        dispatch,
        setToastMessage: () => {},
        device,
      });
    }
  };

  const defaultAction = () => {
    toggleMenu();
    if (isAutoSignUp) {
      setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
      dispatch(showSignUp());
    } else {
      if (isTrueCallerEnabled && !tcInitiated) {
        dispatch(showSignUp());
        setTcInitiated(true);
        setTimeout(() => {
          loadTruecallerScript();
        }, 100);
      } else {
        redirectAuthPage(ACCOUNT.topBar);
      }
    }
  };
  const pageType = getPageType(pathname);

  const handleSmartLogin = () => {
    setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
    pushEvent(LOGIN_CLICK_EVENT, {
      interactionLocation: authPageInteractionLocation(
        pageType,
        ACCOUNT.topBar
      ),
    });
    if (isSmartLockActive) {
      autoSignIn().catch(() => {
        defaultAction();
      });
    } else {
      defaultAction();
    }
  };

  const handleChatClick = () => {
    if (!isIndianUser) {
      setToastMsg(
        "Oh no! We currently don't support this feature at your location"
      );
      setTimeout(() => {
        closeMenu();
      }, 2000);
      return;
    }
    if (isZendeskEnabled) {
      setShowZendeskChat(true);
    } else {
      handleVerloopChat();
    }
    closeMenu();
  };

  //  TODO: handle login for SPA behavior in the future
  return (
    <>
      {menuItems.map((menuItem) => (
        <Item
          key={menuItem.link}
          itemClass={listItemClass}
          link={menuItem.link}
          text={menuItem.displayText}
        />
      ))}
      {showChatNow && (
        <li className={listItemClass}>
          <Button
            type="button"
            onClick={handleChatClick}
            className="link"
          >
            Chat Now
          </Button>
        </li>
      )}
      <li className={listItemClass}>
        <LoginLink
          onClick={() => handleSmartLogin()}
          className="link"
        >
          <FormattedMessage id={LOG_IN} />
        </LoginLink>
      </li>
      {toastMsg && (
        <Toast
          variant={VARIANT.single}
          hideTimer
          message={toastMsg}
          timer={2}
          onDismiss={() => setToastMsg('')}
        />
      )}
    </>
  );
}

export default GuestUser;
